import { API_CONFIG } from "./config";

export type ApiUser = {
  id: number | string;
  name: string;
  phone: string;
  team?: string;
  role: string;
  status: string;
  h5_sales_enabled?: boolean;
  h5_photographer_enabled?: boolean;
  can_take_shooting_order?: boolean;
  permissions?: string[];
  token_total?: number;
  last_login_at?: string | null;
};

export type ApiOption = {
  id: number | string;
  option_type: "brand" | "campus" | "city" | "days" | string;
  type?: string;
  value: string;
  label?: string;
  parent_value?: string | null;
  status?: string;
  sort_order?: number;
};

export type PermissionGroup = {
  group: string;
  items: { code: string; name: string; description?: string; group_name?: string }[];
};

export type ScriptCategoryMeta = {
  name: string;
  count: number;
  sort_order: number;
  pinned: boolean;
};

export type MaterialCategory = {
  id: number | string;
  name: string;
  sort_order?: number;
  status?: string;
  count?: number;
  created_at?: string;
  updated_at?: string;
};

export type MaterialItem = {
  id: number | string;
  category_id?: number | string | null;
  category_name?: string | null;
  title: string;
  description?: string | null;
  material_type: "image" | "text" | string;
  content_text?: string | null;
  file_url?: string | null;
  thumbnail_url?: string | null;
  storage_provider?: string | null;
  object_key?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  sort_order?: number;
  status?: string;
  created_by?: number | string | null;
  created_at?: string;
  updated_at?: string;
};

export type MaterialUploadInfo = {
  file_url: string;
  thumbnail_url?: string;
  storage_provider?: string;
  object_key?: string;
  file_name?: string;
  file_size?: number;
};

export type AppRole = {
  id?: number | string;
  code: string;
  name: string;
  description?: string;
  status?: string;
  is_system?: boolean;
  permissions?: string[];
  user_count?: number;
};

type RolePayload = {
  code?: string;
  name: string;
  description?: string;
  status?: string;
  permissions: string[];
};

export type AISetting = {
  id: number | string;
  provider: string;
  api_base_url: string;
  api_key?: string;
  api_key_masked?: string;
  model_name: string;
  temperature: number;
  status: string;
  created_at?: string;
  updated_at?: string;
};

export type OrderReport = {
  id: number | string;
  report_no?: string;
  user_id?: number | string;
  user_name?: string;
  sales_name?: string;
  brand?: string;
  child_name?: string;
  parent_name?: string;
  phone?: string;
  wechat?: string;
  class_name?: string;
  camp_period?: string;
  report_date?: string;
  days?: string;
  campus?: string;
  package_name?: string;
  order_teacher?: string;
  amount?: number | string;
  payment_status?: string;
  payment_time?: string;
  gift?: string;
  live_link?: string;
  remark?: string;
  status?: string;
  status_name?: string;
  raw_text?: string;
  formatted_text?: string;
  order_staff_note?: string;
  refund_required?: boolean;
  refund_type?: "none" | "full" | "partial" | string;
  refund_amount?: number | string | null;
  refund_paid?: boolean;
  refund_paid_at?: string | null;
  refund_note?: string | null;
  created_at?: string;
  checked_at?: string | null;
  shooting_task?: ShootingTask | null;
  shooting_uploads?: ShootingUpload[];
};

export type BrandNameCheckRow = {
  name: string;
  imported_count?: number;
  system_count?: number;
  orders?: Partial<OrderReport>[];
};

export type BrandNameCheckResult = {
  summary: {
    imported_count: number;
    imported_unique_count: number;
    system_count: number;
    system_unique_count: number;
    matched_count: number;
    missing_count: number;
    extra_count: number;
    duplicate_count: number;
  };
  matched: BrandNameCheckRow[];
  missing: BrandNameCheckRow[];
  extra: BrandNameCheckRow[];
  duplicates: BrandNameCheckRow[];
};

export type ReportChangeLog = {
  id: number | string;
  report_id: number | string;
  operator_id?: number | string | null;
  operator_name?: string | null;
  operator_role?: string | null;
  action: string;
  before_json?: Record<string, unknown> | null;
  after_json?: Record<string, unknown> | null;
  changed_fields?: Record<string, { before?: unknown; after?: unknown }> | null;
  note?: string | null;
  created_at?: string | null;
};

export type ReportDeleteRequest = {
  id: number | string;
  report_id: number | string;
  report?: OrderReport | null;
  requested_by?: number | string;
  requested_by_name?: string | null;
  requested_by_role?: string | null;
  request_reason?: string;
  status?: string;
  reviewed_by?: number | string | null;
  reviewed_by_name?: string | null;
  review_note?: string | null;
  requested_at?: string | null;
  reviewed_at?: string | null;
  deleted_at?: string | null;
};

export type ShootingTask = {
  id: number | string;
  task_no?: string;
  order_report_id?: number | string;
  photographer_id?: number | string | null;
  photographer_name?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  class_name?: string;
  report_date?: string;
  days?: string;
  end_date?: string;
  package_name?: string;
  amount?: number | string;
  special_note?: string;
  shooting_requirement?: string;
  shooting_status?: string;
  shooting_priority?: string;
  review_status?: string;
  created_at?: string;
  updated_at?: string;
};

export type ShootingUpload = {
  id: number | string;
  task_id?: number | string;
  photographer_id?: number | string;
  photographer_name?: string;
  thumbnail_url?: string;
  original_url?: string;
  preview_url?: string;
  storage_provider?: "local" | "cos" | string;
  original_key?: string;
  thumbnail_key?: string;
  preview_key?: string;
  upload_source?: "h5" | "exe" | "legacy" | string;
  upload_status?: string;
  object_etag?: string;
  file_name?: string;
  upload_note?: string;
  review_status?: string;
  review_comment?: string;
  created_at?: string;
  task?: ShootingTask;
};

export type CosUploadPrepare = {
  storage_provider: "cos";
  object_key: string;
  upload_url: string;
  method: "PUT" | string;
  headers?: Record<string, string>;
  file_name: string;
  expires_in: number;
  expires_at: number;
  upload_token: string;
};

export type CosUploadCompletePayload = {
  object_key: string;
  upload_token: string;
  expires_at: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  upload_note?: string;
  upload_source: "exe";
};

export type ShootingTaskDetail = {
  task: ShootingTask;
  uploads?: ShootingUpload[];
};

export type DownloadFileInfo = {
  url: string;
  filename?: string;
};

export type ReviewDownloadJob = {
  id: number | string;
  title: string;
  job_type?: string;
  task_ids?: Array<number | string>;
  status: "queued" | "running" | "completed" | "failed" | string;
  expired?: boolean;
  progress: number;
  total_count?: number;
  finished_count?: number;
  file_url?: string | null;
  file_provider?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  expires_at?: string | null;
  download_count?: number | null;
  downloaded_at?: string | null;
  error_message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
};

export type PageResult<T> = {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
};

export type DispatchReportPage = PageResult<OrderReport> & {
  stats?: {
    total: number;
    assigned: number;
    unassigned: number;
  };
  options?: {
    brands?: string[];
    campuses?: string[];
    packages?: string[];
    days?: string[];
  };
};

export type AdminReportsPageResult = PageResult<OrderReport> & {
  options?: {
    teachers?: string[];
  };
};

export type OrderManagementSummary = {
  checked?: boolean;
  report_status?: string;
  report_status_name?: string;
  dispatch_status?: "assigned" | "unassigned" | string;
  dispatch_status_name?: string;
  photographer_id?: number | string | null;
  photographer_name?: string;
  shooting_status?: string;
  shooting_status_name?: string;
  review_status?: string;
  review_status_name?: string;
  uploaded?: boolean;
  upload_count?: number;
  download_status?: string;
  download_status_name?: string;
  download_count?: number;
  latest_download_job?: ReviewDownloadJob | null;
};

export type OrderManagementRow = OrderReport & {
  status_summary?: OrderManagementSummary;
};

export type OrderManagementPageResult = PageResult<OrderManagementRow> & {
  stats?: {
    duplicate_name_count: number;
    duplicate_order_count: number;
  };
};

export type OrderDeletedRecord = {
  id: number | string;
  report_no?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  class_name?: string;
  package_name?: string;
  created_at?: string | null;
  deleted_at?: string | null;
  deleted_by_name?: string;
  delete_reason?: string;
};

export type OrderDuplicateMeta = {
  shipment_count: number;
  progress_completed_count: number;
  recommendation_score: number;
};

export type OrderDuplicateGroup = {
  name: string;
  normalized_name: string;
  order_count: number;
  risk: "high" | "review" | "possible";
  risk_name: string;
  differences: Array<{ key: string; label: string }>;
  recommended_keep_id?: number | string | null;
  recommendation_reasons: string[];
  orders: Array<OrderManagementRow & { duplicate_meta?: OrderDuplicateMeta }>;
};

export type OrderDuplicateGroupPage = PageResult<OrderDuplicateGroup> & {
  total_groups: number;
  total_orders: number;
  stats: { high: number; review: number; possible: number };
};

export type OrderProgressStage = {
  label: string;
  completed: boolean;
  text: string;
  completed_at?: string | null;
  automatic?: boolean;
  in_progress?: boolean;
  shipment_count?: number;
};

export type OrderProductionProgress = {
  id?: number | string | null;
  order_report_id?: number | string | null;
  stages: Record<string, OrderProgressStage>;
  customer_stages: OrderProgressStage[];
  completed_count: number;
  total_count: number;
  updated_at?: string | null;
  updated_by?: number | string | null;
  updated_by_name?: string | null;
  updated_by_role?: string | null;
  note?: string | null;
};

export type OrderProgressRow = {
  id: number | string;
  report_no?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  class_name?: string;
  package_name?: string;
  created_at?: string | null;
  end_date?: string | null;
  days_since_end?: number | null;
  progress: OrderProductionProgress;
};

export type OrderProgressPage = PageResult<OrderProgressRow> & {
  stats: {
    total: number;
    pending_photographed: number;
    pending_retouched: number;
    pending_layout: number;
    pending_production: number;
    pending_shipped: number;
    ended_15d_unretouched: number;
    ended_15d_unlayout: number;
  };
  options: { brands: string[]; campuses: string[] };
  capabilities: Record<string, boolean>;
};

export type OrderProgressImportCandidate = {
  id: number;
  report_no?: string;
  child_name?: string;
  brand?: string;
  campus?: string;
  class_name?: string;
  package_name?: string;
  target_completed: boolean;
  missing_upstream: string[];
  missing_upstream_names: string[];
};

export type OrderProgressImportRow = {
  row_no: number;
  child_name: string;
  report_no?: string | null;
  class_name?: string | null;
  order_report_id?: number | null;
  candidates: OrderProgressImportCandidate[];
  status: "matched" | "ambiguous" | "unmatched" | "duplicate" | "invalid" | "already_completed" | "prerequisite_missing";
  message: string;
};

export type OrderProgressImportPreview = {
  rows: OrderProgressImportRow[];
  summary: Record<string, number> & { total: number };
  stage: string;
  stage_name: string;
};

export type OrderProgressBatch = {
  batch_id: string;
  source: "import" | "selection";
  stage?: string | null;
  stage_name: string;
  completed: boolean;
  updated_count: number;
  cascade_update_count: number;
  operator_id?: number | string | null;
  operator_name: string;
  created_at?: string | null;
  undone: boolean;
  undone_at?: string | null;
  undone_by_name?: string | null;
  can_undo: boolean;
};

export type PhotographerOverviewItem = ApiUser & {
  scope?: any;
  stats?: {
    total_tasks?: number;
    today_tasks?: number;
    today_shot?: number;
    pending_review?: number;
    issue?: number;
    near_end?: number;
    upload_count?: number;
    approved?: number;
    rejected?: number;
    upload_pending?: number;
    latest_upload?: string;
    no_scope?: boolean;
  };
  tasks?: ShootingTask[];
  uploads?: ShootingUpload[];
};

export type PhotographerOverview = {
  items: PhotographerOverviewItem[];
  stats: {
    total: number;
    today_pending: number;
    today_shot: number;
    pending_review: number;
    rejected: number;
    near_end: number;
    tomorrow_plan?: string | number;
    no_scope: number;
  };
};

export type AuditLog = {
  id: string;
  source: string;
  module: "系统" | "报单" | "拍摄" | "审图" | "训练" | "考试" | string;
  title: string;
  operator_id?: number | string | null;
  operator_name?: string | null;
  operator_role?: string | null;
  target_type?: string | null;
  target_id?: number | string | null;
  target_label?: string | null;
  note?: string | null;
  detail?: Record<string, unknown> | null;
  ip_address?: string | null;
  created_at?: string | null;
};

export type UserNotification = {
  id: number | string;
  user_id?: number | string;
  type: string;
  title: string;
  content: string;
  related_type?: string | null;
  related_id?: number | string | null;
  detail?: Record<string, unknown>;
  is_read?: boolean;
  read_at?: string | null;
  created_at?: string | null;
};

export type Exam = {
  id: number | string;
  title: string;
  level?: string;
  description?: string;
  total_score?: number;
  pass_score?: number;
  questions?: ExamQuestion[];
};

export type ChatMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

export type ExamQuestion = {
  id: number | string;
  exam_id?: number | string;
  question_type?: string;
  type?: string;
  title?: string;
  stem?: string;
  content?: string;
  options_json?: string[] | Record<string, string>;
  options?: string[] | Record<string, string>;
  score?: number;
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const TOKEN_KEY = "yzb_access_token";
const USER_KEY = "yzb_current_user";
const SENSITIVE_ERROR_PATTERN =
  /(traceback|sqlalchemy|mysql|token|authorization|bearer\s+|secret|api[_-]?key|upload_url|upload_token|password|https?:\/\/|127\.0\.0\.1|localhost|81\.71\.153\.47|cos)/i;

let accessToken = localStorage.getItem(TOKEN_KEY) || "";

function friendly(status: number, message: string) {
  const text = String(message || "");
  const safeMessage = SENSITIVE_ERROR_PATTERN.test(text) ? "" : text;
  if (status === 0) return "网络连接失败，请稍后重试";
  if (status === 401) return "登录已过期，请重新登录";
  if (status === 403) return "当前账号无权限";
  if (status === 422) return "\u53c2\u6570\u683c\u5f0f\u4e0d\u6b63\u786e\uff0c\u8bf7\u68c0\u67e5\u540e\u91cd\u8bd5";
  if (safeMessage?.includes("pending")) return "账号正在审核中，请联系管理员";
  if (safeMessage?.includes("disabled")) return "账号已被禁用，请联系管理员";
  if (safeMessage?.includes("rejected")) return "账号审核未通过，请联系管理员";
  if (status >= 500) return "服务器暂时不可用，请联系管理员";
  return safeMessage || "请求失败，请稍后重试";
}

function cleanRolePayload(data: Partial<AppRole>, includeCode = false): RolePayload {
  const permissions = Array.from(
    new Set(
      (Array.isArray(data.permissions) ? data.permissions : [])
        .map(item => String(item || "").trim())
        .filter(Boolean)
    )
  );
  const payload: RolePayload = {
    name: String(data.name || "").trim(),
    description: data.description ? String(data.description).trim() : "",
    status: data.status === "disabled" ? "disabled" : "enabled",
    permissions,
  };
  if (includeCode) payload.code = String(data.code || "").trim();
  return payload;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

  let res: Response;
  try {
    res = await fetch(`${API_CONFIG.baseUrl}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, friendly(0, ""));
  }

  let body: any = null;
  const text = await res.text();
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = { message: text };
  }

  if (!res.ok || body?.success === false) {
    if (res.status === 401) api.logout();
    if (res.status === 404 && path.includes("/reviewer/download-jobs")) {
      throw new ApiError(res.status, "服务器下载中心接口还没更新，请先发布后端代码、导入下载中心 SQL 并重启后端服务。");
    }
    throw new ApiError(res.status, friendly(res.status, body?.message || body?.detail));
  }
  return (body && "data" in body ? body.data : body) as T;
}

function filenameFromDisposition(value: string | null, fallback: string) {
  if (!value) return fallback;
  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return fallback;
    }
  }
  const match = value.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

async function openDownloadUrl(url: string, filename = "") {
  const href = url.startsWith("http") ? url : `${API_CONFIG.baseUrl.replace(/\/api$/, "")}${url}`;
  const isTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
  if (isTauri) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("open_external_url", { url: href });
      return;
    } catch {
      // Fall back to the browser-style download trigger below.
    }
  }
  const a = document.createElement("a");
  a.href = href;
  if (filename) a.download = filename;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function downloadBlob(path: string, init: RequestInit = {}, fallbackName = "download.zip") {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  let res: Response;
  try {
    res = await fetch(`${API_CONFIG.baseUrl}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, friendly(0, ""));
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let body: any = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { message: text };
    }
    if (res.status === 401) api.logout();
    throw new ApiError(res.status, friendly(res.status, body?.message || body?.detail));
  }
  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get("Content-Disposition"), fallbackName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return filename;
}

function qs(params: Record<string, unknown>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== "") query.set(key, String(value));
  });
  const s = query.toString();
  return s ? `?${s}` : "";
}

function normalizeImageMimeType(file: File) {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  return "image/jpeg";
}

function uploadFileToCos(prepared: CosUploadPrepare, file: File, onProgress?: (progress: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(prepared.method || "PUT", prepared.upload_url);
    xhr.timeout = 300000;
    Object.entries(prepared.headers || {}).forEach(([key, value]) => {
      if (key.toLowerCase() === "content-type") return;
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.max(1, Math.min(95, Math.round((event.loaded / event.total) * 95))));
    };
    xhr.onerror = () => reject(new ApiError(0, "上传失败，请重试"));
    xhr.ontimeout = () => reject(new ApiError(0, "上传超时，请检查网络后重试"));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(96);
        resolve();
        return;
      }
      reject(new ApiError(xhr.status, "上传失败，请重试"));
    };
    xhr.send(file);
  });
}

export const api = {
  baseUrl: API_CONFIG.baseUrl,
  token: () => accessToken,
  storedUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null") as ApiUser | null;
    } catch {
      return null;
    }
  },
  setSession(token: string, user: ApiUser) {
    accessToken = token;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  logout() {
    accessToken = "";
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  async login(phone: string, password: string) {
    const data = await request<{ access_token: string; user: ApiUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ phone, password }),
    });
    api.setSession(data.access_token, data.user);
    return data.user;
  },
  register: (data: { name: string; phone: string; password: string; role?: string; team?: string; remark?: string }) =>
    request<{ status: string }>("/auth/register", { method: "POST", body: JSON.stringify(data) }),
  me: async () => {
    const user = await request<ApiUser>("/auth/me");
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  },
  dashboard: () => request<any>("/dashboard").catch(() => null),

  users: (params: Record<string, unknown> = {}) => request<ApiUser[]>(`/admin/users${qs(params)}`),
  updateUser: (id: string | number, data: Record<string, unknown>) =>
    request<ApiUser>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  createUser: (data: Record<string, unknown>) =>
    request<ApiUser>("/admin/users", { method: "POST", body: JSON.stringify(data) }),
  roles: () => request<AppRole[]>("/admin/roles"),
  permissions: () => request<PermissionGroup[]>("/admin/roles/permissions"),
  createRole: (data: Partial<AppRole>) =>
    request<AppRole>("/admin/roles", { method: "POST", body: JSON.stringify(cleanRolePayload(data, true)) }),
  updateRole: (code: string, data: Partial<AppRole>) =>
    request<AppRole>(`/admin/roles/${encodeURIComponent(code)}`, { method: "PATCH", body: JSON.stringify(cleanRolePayload(data)) }),
  deleteRole: (code: string) => request<{ code: string }>(`/admin/roles/${code}`, { method: "DELETE" }),
  aiSettings: () => request<AISetting[]>("/admin/ai-settings"),
  createAISetting: (data: Partial<AISetting>) =>
    request<AISetting>("/admin/ai-settings", { method: "POST", body: JSON.stringify(data) }),
  updateAISetting: (id: string | number, data: Partial<AISetting>) =>
    request<AISetting>(`/admin/ai-settings/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  enableAISetting: (id: string | number) => request<AISetting>(`/admin/ai-settings/${id}/enable`, { method: "POST" }),
  disableAISetting: (id: string | number) => request<AISetting>(`/admin/ai-settings/${id}/disable`, { method: "POST" }),
  deleteAISetting: (id: string | number) => request<{ id: string | number }>(`/admin/ai-settings/${id}`, { method: "DELETE" }),
  testAISetting: (id: string | number) => request<any>(`/admin/ai-settings/${id}/test`, { method: "POST" }),

  reportOptions: (type?: string) => request<ApiOption[]>(`/report-options${qs({ option_type: type })}`),
  adminReportOptions: (type?: string) => request<ApiOption[]>(`/admin/report-options${qs({ option_type: type })}`),
  createReportOption: (data: Partial<ApiOption>) =>
    request<ApiOption>("/admin/report-options", { method: "POST", body: JSON.stringify(data) }),
  updateReportOption: (id: string | number, data: Partial<ApiOption>) =>
    request<ApiOption>(`/admin/report-options/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteReportOption: (id: string | number) => request<boolean>(`/admin/report-options/${id}`, { method: "DELETE" }),

  materialCategories: () => request<MaterialCategory[]>("/materials/categories"),
  materials: (params: Record<string, unknown> = {}) => request<PageResult<MaterialItem>>(`/materials${qs(params)}`),
  adminMaterialCategories: () => request<MaterialCategory[]>("/admin/material-categories"),
  createMaterialCategory: (data: Partial<MaterialCategory>) =>
    request<MaterialCategory>("/admin/material-categories", { method: "POST", body: JSON.stringify(data) }),
  updateMaterialCategory: (id: string | number, data: Partial<MaterialCategory>) =>
    request<MaterialCategory>(`/admin/material-categories/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteMaterialCategory: (id: string | number) =>
    request<boolean>(`/admin/material-categories/${id}`, { method: "DELETE" }),
  adminMaterials: (params: Record<string, unknown> = {}) => request<PageResult<MaterialItem>>(`/admin/materials${qs(params)}`),
  createMaterial: (data: Partial<MaterialItem>) =>
    request<MaterialItem>("/admin/materials", { method: "POST", body: JSON.stringify(data) }),
  updateMaterial: (id: string | number, data: Partial<MaterialItem>) =>
    request<MaterialItem>(`/admin/materials/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteMaterial: (id: string | number) => request<boolean>(`/admin/materials/${id}`, { method: "DELETE" }),
  backfillMaterialThumbnails: (limit = 100) =>
    request<{ checked: number; updated: number; skipped: number; failed: number }>(`/admin/materials/backfill-thumbnails${qs({ limit })}`, { method: "POST" }),
  uploadMaterialImage: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<MaterialUploadInfo>("/admin/materials/upload", { method: "POST", body });
  },

  createReport: (data: Partial<OrderReport>) =>
    request<OrderReport>("/reports", { method: "POST", body: JSON.stringify(data) }),
  myReports: (params: Record<string, unknown> = {}) => request<OrderReport[]>(`/reports/my${qs(params)}`),
  updateMyReport: (id: string | number, data: Partial<OrderReport>) =>
    request<OrderReport>(`/reports/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  adminReports: (params: Record<string, unknown> = {}) => request<OrderReport[]>(`/admin/reports${qs(params)}`),
  adminReportsPage: (params: Record<string, unknown> = {}) => request<AdminReportsPageResult>(`/admin/reports${qs(params)}`),
  dispatchReportsPage: (params: Record<string, unknown> = {}) => request<DispatchReportPage>(`/admin/reports/dispatch${qs(params)}`),
  adminOrdersPage: (params: Record<string, unknown> = {}) => request<OrderManagementPageResult>(`/admin/orders${qs(params)}`),
  orderDuplicateGroups: (params: Record<string, unknown> = {}) => request<OrderDuplicateGroupPage>(`/admin/orders/duplicate-groups${qs(params)}`),
  adminOrderDetail: (id: string | number) => request<OrderManagementRow>(`/admin/orders/${id}`),
  orderProgressPage: (params: Record<string, unknown> = {}) => request<OrderProgressPage>(`/admin/order-progress${qs(params)}`),
  updateOrderProgressStage: (id: string | number, stage: string, completed: boolean, reason = "") =>
    request<OrderProgressRow>(`/admin/order-progress/${id}/stage`, {
      method: "PATCH",
      body: JSON.stringify({ stage, completed, reason }),
    }),
  batchUpdateOrderProgress: (orderIds: Array<string | number>, stage: string, completed: boolean, reason = "") =>
    request<{ batch_id: string; updated: number; updated_ids: Array<number | string>; cascade_updated: number; skipped: Array<{ order_id: number | string; reason: string }> }>("/admin/order-progress/batch", {
      method: "POST",
      body: JSON.stringify({ order_ids: orderIds.map(Number).filter(Boolean), stage, completed, reason }),
    }),
  previewOrderProgressImport: (data: Record<string, unknown>) =>
    request<OrderProgressImportPreview>("/admin/order-progress/import-preview", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  commitOrderProgressImport: (data: Record<string, unknown>) =>
    request<{ batch_id: string; updated: number; updated_ids: number[]; cascade_updated: number; skipped: Array<{ row_no: number; child_name?: string; reason: string }> }>("/admin/order-progress/import-commit", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  orderProgressBatches: (limit = 30) =>
    request<OrderProgressBatch[]>(`/admin/order-progress/batches${qs({ limit })}`),
  undoOrderProgressBatch: (batchId: string, reason: string) =>
    request<{ batch_id: string; restored_orders: number; restored_stages: number; skipped: Array<{ order_id: number | string; stage: string; reason: string }> }>(`/admin/order-progress/batches/${encodeURIComponent(batchId)}/undo`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  orderProgressLogs: (id: string | number) => request<any[]>(`/admin/order-progress/${id}/logs`),
  updateAdminOrder: (id: string | number, data: Partial<OrderReport>) =>
    request<OrderManagementRow>(`/admin/orders/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteAdminOrder: (id: string | number, reason = "") => request<Record<string, unknown>>(`/admin/orders/${id}`, { method: "DELETE", body: JSON.stringify({ note: reason || undefined }) }),
  deletedAdminOrders: (limit = 50) => request<OrderDeletedRecord[]>(`/admin/orders/deleted${qs({ limit })}`),
  restoreAdminOrder: (id: string | number, reason: string) => request<Record<string, unknown>>(`/admin/orders/${id}/restore`, { method: "POST", body: JSON.stringify({ reason }) }),
  reportStats: () => request<any>("/admin/reports/stats"),
  updateReport: (id: string | number, data: Partial<OrderReport>) =>
    request<OrderReport>(`/admin/reports/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  reportAction: (id: string | number, action: "check" | "return" | "void", note = "") =>
    request<OrderReport>(`/admin/reports/${id}/${action}`, { method: "POST", body: JSON.stringify({ note }) }),
  brandNameCheck: (data: { brand: string; names_text: string; report_date?: string; campus?: string; status?: string }) =>
    request<BrandNameCheckResult>("/admin/reports/brand-name-check", { method: "POST", body: JSON.stringify(data) }),
  reportChangeLogs: (id: string | number) => request<ReportChangeLog[]>(`/admin/reports/${id}/change-logs`),
  requestReportDelete: (id: string | number, reason: string) =>
    request<ReportDeleteRequest>(`/reports/${id}/delete-request`, { method: "POST", body: JSON.stringify({ reason }) }),
  adminReportDeleteRequests: (params: Record<string, unknown> = { status: "pending" }) =>
    request<ReportDeleteRequest[]>(`/admin/reports/delete-requests${qs(params)}`),
  approveReportDeleteRequest: (id: string | number, note = "") =>
    request<ReportDeleteRequest>(`/admin/reports/delete-requests/${id}/approve`, { method: "POST", body: JSON.stringify({ note }) }),
  rejectReportDeleteRequest: (id: string | number, note = "") =>
    request<ReportDeleteRequest>(`/admin/reports/delete-requests/${id}/reject`, { method: "POST", body: JSON.stringify({ note }) }),
  parseReport: (raw_text: string) =>
    request<Partial<OrderReport> & { missing_fields?: string[]; confidence?: number }>("/reports/parse", {
      method: "POST",
      body: JSON.stringify({ raw_text }),
    }),
  formatReport: (data: Partial<OrderReport>) =>
    request<{ formatted_text: string }>("/reports/format", { method: "POST", body: JSON.stringify(data) }),

  scripts: () => request<any[]>("/scripts"),
  scriptCategories: () => request<string[]>("/scripts/categories"),
  scriptCategoryMeta: async () => {
    try {
      return await request<ScriptCategoryMeta[]>("/admin/scripts/categories/meta");
    } catch {
      const names = await request<string[]>("/scripts/categories");
      return names.map(name => ({ name, count: 0, sort_order: 0, pinned: false }));
    }
  },
  createScriptCategory: (name: string) =>
    request<any>("/admin/scripts/categories", { method: "POST", body: JSON.stringify({ name }) }),
  renameScriptCategory: (name: string, nextName: string) =>
    request<any>(`/admin/scripts/categories/${encodeURIComponent(name)}`, { method: "PATCH", body: JSON.stringify({ name: nextName }) }),
  pinScriptCategory: (name: string, pinned: boolean) =>
    request<any>(`/admin/scripts/categories/${encodeURIComponent(name)}/pin`, { method: "PATCH", body: JSON.stringify({ pinned }) }),
  mergeSimilarScriptCategories: () => request<any>("/admin/scripts/categories/merge-similar", { method: "POST" }),
  deleteScriptCategory: (name: string, deleteScripts = false) =>
    request<any>(`/admin/scripts/categories/${encodeURIComponent(name)}${qs({ delete_scripts: deleteScripts })}`, { method: "DELETE" }),
  importScripts: (scripts: Array<{ category: string; title: string; content: string; sort_order?: number; status?: string }>) =>
    request<{ created: number; skipped: number }>("/admin/scripts/import", { method: "POST", body: JSON.stringify({ scripts }) }),
  createScript: (data: Record<string, unknown>) =>
    request<any>("/admin/scripts", { method: "POST", body: JSON.stringify(data) }),
  updateScript: (id: string | number, data: Record<string, unknown>) =>
    request<any>(`/admin/scripts/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  updateScriptStatus: (id: string | number, status: string) =>
    request<any>(`/admin/scripts/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  deleteScript: (id: string | number) => request<any>(`/admin/scripts/${id}`, { method: "DELETE" }),
  aiChat: (messages: ChatMessage[], max_tokens = 800, temperature?: number) =>
    request<{ answer: string; model_name?: string; usage?: any }>("/ai/chat", {
      method: "POST",
      body: JSON.stringify({ messages, max_tokens, temperature }),
    }),

  exams: () => request<Exam[]>("/exams"),
  examDetail: (id: string | number) => request<Exam>(`/exams/${id}`),
  examAttempts: () => request<any[]>("/exam-attempts/my"),
  startExam: (id: string | number) => request<any>(`/exams/${id}/start`, { method: "POST" }),
  submitExam: (id: string | number, answers: unknown) =>
    request<any>(`/exam-attempts/${id}/submit`, { method: "POST", body: JSON.stringify({ answers }) }),
  adminExamAttempts: (params: Record<string, unknown> = {}) => request<any[]>(`/admin/exam-attempts${qs(params)}`),
  auditLogs: (params: Record<string, unknown> = {}) => request<AuditLog[]>(`/admin/audit-logs${qs(params)}`),
  notifications: (params: Record<string, unknown> = {}) => request<UserNotification[]>(`/notifications${qs(params)}`),
  readNotification: (id: string | number) =>
    request<UserNotification>(`/notifications/${id}/read`, { method: "POST", body: JSON.stringify({}) }),
  readAllNotifications: () => request<{ count: number }>("/notifications/read-all", { method: "POST", body: JSON.stringify({}) }),

  trainingRoles: () => request<any[]>("/training-roles"),
  trainingSessions: () => request<any[]>("/training-sessions/my").catch(() => []),
  createTrainingSession: (data: Record<string, unknown>) =>
    request<any>("/training-sessions", { method: "POST", body: JSON.stringify(data) }),

  shootingPhotographers: () => request<ApiUser[]>("/admin/shooting-tasks/photographers"),
  photographerOverview: (params: Record<string, unknown> = {}) => request<PhotographerOverview>(`/admin/photographers/overview${qs(params)}`),
  shootingTasks: (params: Record<string, unknown> = {}) => request<ShootingTask[]>(`/admin/shooting-tasks${qs(params)}`),
  shootingTasksPage: (params: Record<string, unknown> = {}) => request<PageResult<ShootingTask>>(`/admin/shooting-tasks${qs(params)}`),
  shootingStats: () => request<any>("/admin/shooting-tasks/stats"),
  shootingWeather: (params: Record<string, unknown> = {}) => request<any>(`/admin/shooting-weather${qs(params)}`),
  batchGenerateShootingTasks: (order_report_ids: Array<string | number> = [], photographer_id?: string | number) =>
    request<any>("/admin/shooting-tasks/batch-generate", {
      method: "POST",
      body: JSON.stringify({
        order_report_ids: order_report_ids.map(Number).filter(Boolean),
        photographer_id: photographer_id ? Number(photographer_id) : undefined,
      }),
    }),
  assignShootingTask: (id: string | number, photographer_id: string | number, note = "") =>
    request<ShootingTask>(`/admin/shooting-tasks/${id}/assign`, {
      method: "POST",
      body: JSON.stringify({ photographer_id: Number(photographer_id), note }),
    }),
  batchAssignShootingTasks: (items: Array<{ task_id: string | number; photographer_id: string | number; note?: string }>) =>
    request<{ updated: number; items: ShootingTask[] }>("/admin/shooting-tasks/batch-assign", {
      method: "POST",
      body: JSON.stringify({ items: items.map(item => ({ ...item, task_id: Number(item.task_id), photographer_id: Number(item.photographer_id) })) }),
    }),
  balancedAssignShootingTasks: (
    taskIds: Array<string | number>,
    assignments: Array<{ photographer_id: string | number; count: number }>,
    note = "智能均衡派单",
  ) =>
    request<{ updated: number; skipped: number; items: ShootingTask[]; summary: Array<{ photographer_id: number | string; photographer_name: string; count: number }>; skipped_reasons?: Array<{ task_id: number | string; reason: string }> }>("/admin/shooting-tasks/balanced-assign", {
      method: "POST",
      body: JSON.stringify({
        task_ids: taskIds.map(Number).filter(Boolean),
        assignments: assignments.map(item => ({ photographer_id: Number(item.photographer_id), count: Number(item.count || 0) })),
        note,
      }),
    }),
  remindShootingTask: (id: string | number, note = "") =>
    request<ShootingTask>(`/admin/shooting-tasks/${id}/remind`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  photographerScopes: () => request<any[]>("/admin/photographer-scopes"),
  savePhotographerScope: (id: string | number, data: Record<string, unknown>) =>
    request<any>(`/admin/photographer-scopes/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  savePhotographerDispatchSettings: (id: string | number, data: Record<string, unknown>) =>
    request<any>(`/admin/photographer-dispatch-settings/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  myShootingTasks: () => request<ShootingTask[]>("/photographer/tasks"),
  myShootingStats: () => request<any>("/photographer/tasks/stats").catch(() => null),
  myShootingTaskDetail: (id: string | number) => request<ShootingTaskDetail>(`/photographer/tasks/${id}`),
  markMyShootingTaskShot: (id: string | number, note = "") =>
    request<ShootingTask>(`/photographer/tasks/${id}/mark-shot`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  unmarkMyShootingTaskShot: (id: string | number, note = "") =>
    request<ShootingTask>(`/photographer/tasks/${id}/unmark-shot`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  markMyShootingTaskIssue: (id: string | number, note: string) =>
    request<ShootingTask>(`/photographer/tasks/${id}/mark-issue`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  prepareMyShootingTaskPhotoUpload: (id: string | number, file: File) =>
    request<CosUploadPrepare>(`/photographer/tasks/${id}/uploads/prepare`, {
      method: "POST",
      body: JSON.stringify({
        file_name: file.name || "image.jpg",
        file_size: file.size,
        mime_type: normalizeImageMimeType(file),
        upload_source: "exe",
      }),
    }),
  completeMyShootingTaskPhotoUpload: (
    id: string | number,
    prepared: CosUploadPrepare,
    file: File,
    upload_note = "",
  ) => {
    const payload: CosUploadCompletePayload = {
      object_key: prepared.object_key,
      upload_token: prepared.upload_token,
      expires_at: prepared.expires_at,
      file_name: file.name || prepared.file_name || "image.jpg",
      file_size: file.size,
      mime_type: normalizeImageMimeType(file),
      upload_source: "exe",
    };
    if (upload_note) payload.upload_note = upload_note;
    return request<ShootingUpload>(`/photographer/tasks/${id}/uploads/complete`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  uploadMyShootingTaskPhotoLegacy: (id: string | number, file: File, upload_note = "") => {
    const body = new FormData();
    body.append("file", file);
    if (upload_note) body.append("upload_note", upload_note);
    return request<ShootingUpload>(`/photographer/tasks/${id}/upload`, {
      method: "POST",
      body,
    });
  },
  uploadMyShootingTaskPhoto: async (
    id: string | number,
    file: File,
    upload_note = "",
    onProgress?: (progress: number) => void,
  ) => {
    onProgress?.(1);
    const prepared = await api.prepareMyShootingTaskPhotoUpload(id, file);
    onProgress?.(3);
    await uploadFileToCos(prepared, file, onProgress);
    const upload = await api.completeMyShootingTaskPhotoUpload(id, prepared, file, upload_note);
    onProgress?.(100);
    return upload;
  },
  deleteMyShootingTaskPhoto: (taskId: string | number, uploadId: string | number) =>
    request<{ deleted: boolean; remaining: number }>(`/photographer/tasks/${taskId}/uploads/${uploadId}`, { method: "DELETE" }),

  reviewerUploads: (params: Record<string, unknown> = {}) => request<ShootingUpload[]>(`/reviewer/uploads${qs(params)}`),
  reviewerUploadsPage: (params: Record<string, unknown> = {}) => request<PageResult<ShootingUpload>>(`/reviewer/uploads${qs(params)}`),
  reviewerStats: () => request<any>("/reviewer/stats"),
  reviewDownloadJobs: () => request<ReviewDownloadJob[]>("/reviewer/download-jobs"),
  createReviewDownloadJob: (taskIds: Array<string | number>, title = "", scope: "all" | "approved" = "all") =>
    request<ReviewDownloadJob>("/reviewer/download-jobs", {
      method: "POST",
      body: JSON.stringify({ task_ids: taskIds.map(id => Number(id)).filter(Boolean), title, scope }),
    }),
  retryReviewDownloadJob: (id: string | number) =>
    request<ReviewDownloadJob>(`/reviewer/download-jobs/${id}/retry`, { method: "POST", body: JSON.stringify({}) }),
  reviewDownloadJobUrl: (id: string | number) =>
    request<DownloadFileInfo & { expires_in?: number }>(`/reviewer/download-jobs/${id}/download-url`, { method: "POST", body: JSON.stringify({}) }),
  openDownloadUrl,
  downloadReviewTaskOriginals: async (id: string | number) => {
    const info = await request<DownloadFileInfo>(`/reviewer/tasks/${id}/download-originals`);
    await openDownloadUrl(info.url, info.filename);
    return info;
  },
  downloadReviewTasksOriginals: async (taskIds: Array<string | number>, scope: "all" | "approved" = "all") => {
    const info = await request<DownloadFileInfo>("/reviewer/tasks/download-originals", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ task_ids: taskIds.map(id => Number(id)).filter(Boolean), scope }),
    });
    await openDownloadUrl(info.url, info.filename);
    return info;
  },
  approveUpload: (id: string | number) =>
    request<any>(`/reviewer/uploads/${id}/approve`, { method: "POST", body: JSON.stringify({ review_comment: "" }) }),
  rejectUpload: (id: string | number, review_comment: string) =>
    request<any>(`/reviewer/uploads/${id}/reject`, { method: "POST", body: JSON.stringify({ review_comment }) }),
};

export function can(user: ApiUser | null, permission: string) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return Boolean(user.permissions?.includes(permission));
}
